<template>
  <VModal :showing="open" container-classes="max-w-md" @update:showing="onOpenChange">
    <template #header>
      <div class="flex items-center gap-2 text-lg font-semibold text-gray-900">
        <FontAwesomeIcon icon="newspaper" class="w-5 h-5 text-gray-600" />
        Press Release
      </div>
    </template>
    <template #default>
      <div v-if="!link" class="flex flex-col gap-4">
        <p class="text-xs text-gray-500 mb-4">
          Manage the press release link for this 3D Presentation. Anyone with the link can access it even when General Access is restricted.
        </p>
        <div class="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-6 py-10 flex flex-col items-center text-center gap-3">
          <FontAwesomeIcon icon="newspaper" class="w-10 h-10 text-gray-400" />
          <p class="font-semibold text-gray-900">No link created yet</p>
          <p class="text-sm text-gray-600 max-w-sm">
            A press release link will allow <strong>anyone with the link to access this 3D Presentation</strong>, even if it is currently
            restricted by the General Access setting.
          </p>
        </div>
        <VButton class="w-full justify-center" variant="copper" :disabled="creating" @click="handleCreate">
          <FontAwesomeIcon icon="link" class="w-4 h-4 mr-2" />
          Create link
        </VButton>
      </div>

      <div v-else class="flex flex-col gap-4">
        <div class="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-b border-gray-200 py-2">
          <span class="inline-flex items-center gap-2">
            <span class="w-2 h-2 shrink-0 rounded-full bg-emerald-500" aria-hidden="true" />
            <span class="text-sm font-medium text-emerald-700">Live</span>
          </span>
          <span class="text-sm text-gray-500">Since {{ formatDateMed(link.created_at) }}</span>
        </div>

        <div class="space-y-2">
          <label for="press-release-url" class="text-sm font-medium text-gray-900">Press Release Link</label>
          <div class="flex gap-2">
            <VInput id="press-release-url" class="font-mono text-xs flex-1 min-w-0" readonly :model-value="stakeholderUrl" />
            <VButton type="button" variant="copper-outline" size="md" class="shrink-0" @click="handleCopy">
              <FontAwesomeIcon icon="link" class="w-4 h-4 mr-2" />
              {{ wasClicked ? 'Link copied to clipboard' : 'Copy' }}
            </VButton>
          </div>
          <p class="text-xs text-gray-500">
            Anyone with this link can access this 3D Presentation. This link bypasses all General Access settings. Use Delete Link to
            permanently disable this link.
          </p>
        </div>

        <hr class="border-gray-200" />

        <VButton
          variant="flat"
          class="!text-sm text-red-700 hover_bg-red-50 justify-start border-0 shadow-none"
          @click="destroyConfirmShowing = true"
        >
          <FontAwesomeIcon icon="trash" class="w-4 h-4 mr-2" />
          Delete Link
        </VButton>
      </div>
    </template>
  </VModal>

  <Alert v-model:showing="destroyConfirmShowing" variant="danger" confirmation-label="Delete Link" @confirm="handleDestroyConfirm">
    <template #title>Delete this link?</template>
    This link will stop working immediately, and anyone who has it will lose access. This action cannot be undone.
  </Alert>
</template>

<script setup lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { FetchError } from 'ofetch'
import type { DeepReadonly } from 'vue'
import type { EditDeck } from '~/composables/useEditDeck'
import { formatDateMed } from '@/lib/datetime-format-utils'
import Alert from '~/components/modals/Alert.vue'
import useCopyToClipboard from '~/composables/useCopyToClipboard'

const props = defineProps<{
  deck: DeepReadonly<EditDeck>
}>()

const open = defineModel<boolean>('open', { default: false })

const emit = defineEmits<{
  'refresh-deck': []
}>()

const config = useRuntimeConfig()
const { createPressReleaseLink, deletePressReleaseLink } = useDeckPressRelease()
const { setNotification } = useGlobalNotification()
const { copyToClipboard, wasClicked } = useCopyToClipboard()

const creating = ref(false)
const destroyConfirmShowing = ref(false)

const link = computed(() => props.deck.press_release_link ?? null)

const stakeholderUrl = computed(() => {
  if (!link.value) return ''
  return `${config.public.WEB_ENDPOINT}decks/${props.deck.id}?auth=${link.value.uuid}`
})

function onOpenChange(next: boolean) {
  open.value = next
}

async function handleCreate() {
  creating.value = true
  try {
    await createPressReleaseLink(props.deck.id)
    emit('refresh-deck')
    setNotification({
      type: 'success',
      title: 'Link created - This 3D Presentation is now accessible to anyone with the link.',
      delay: 5000,
    })
  } catch (e) {
    const msg =
      e instanceof FetchError && e.data && typeof (e.data as { message?: string }).message === 'string'
        ? String((e.data as { message: string }).message)
        : 'Could not create press release link.'
    setNotification({
      type: 'danger',
      title: 'Create link failed',
      text: msg,
      delay: 5000,
    })
  } finally {
    creating.value = false
  }
}

function handleCopy() {
  if (!stakeholderUrl.value) return
  copyToClipboard({ copyText: stakeholderUrl.value })
}

async function handleDestroyConfirm() {
  destroyConfirmShowing.value = false
  try {
    await deletePressReleaseLink(props.deck.id)
    emit('refresh-deck')
    setNotification({
      type: 'success',
      title: 'Link deleted - Access via this link has been permanently removed.',
      delay: 5000,
    })
    open.value = false
  } catch (e) {
    const status = e instanceof FetchError ? e.response?.status : undefined
    if (status === 404) {
      emit('refresh-deck')
      open.value = false
      return
    }
    const msg =
      e instanceof FetchError && e.data && typeof (e.data as { message?: string }).message === 'string'
        ? String((e.data as { message: string }).message)
        : 'Could not remove press release link.'
    setNotification({
      type: 'danger',
      title: 'Remove link failed',
      text: msg,
      delay: 5000,
    })
  }
}
</script>
